import React from "react";
import { Check, ChevronDown, CreditCard, FileCheck2, FileText } from "lucide-react";
import { Divider, Fade, Menu } from "@mui/material";
import UIButton from "@reusables/UIButton";
import { useTranslation } from "react-i18next";
import { SaleOrderSubmitIntent } from "../types";

interface CreateOrderSubmitActionsProps {
    onSubmitIntent: (intent: SaleOrderSubmitIntent) => void;
}

export default function CreateOrderSubmitActions(props: CreateOrderSubmitActionsProps) {
    const { onSubmitIntent } = props;
    const { t } = useTranslation("", { keyPrefix: "sales.orders" });

    const anchorEl = React.useRef<HTMLDivElement>(null);
    const closeTimeoutRef = React.useRef<number | null>(null);

    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [hoveredIntent, setHoveredIntent] = React.useState<SaleOrderSubmitIntent | null>(null);

    const clearCloseTimeout = React.useCallback(() => {
        if (closeTimeoutRef.current) {
            window.clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    }, []);

    const openMenu = React.useCallback(() => {
        clearCloseTimeout();
        setIsMenuOpen(true);
    }, [clearCloseTimeout]);

    const closeMenu = React.useCallback(() => {
        clearCloseTimeout();
        setIsMenuOpen(false);
        setHoveredIntent(null);
    }, [clearCloseTimeout]);

    const scheduleClose = React.useCallback(
        (delay = 90) => {
            clearCloseTimeout();
            closeTimeoutRef.current = window.setTimeout(() => {
                setIsMenuOpen(false);
            }, delay);
        },
        [clearCloseTimeout]
    );

    React.useEffect(() => {
        return () => {
            clearCloseTimeout();
        };
    }, [clearCloseTimeout]);

    return (
        <div className="flex flex-wrap items-start justify-center gap-[24px] pt-[8px]">
            <UIButton
                type="button"
                variant="outline"
                className="min-h-[48px] w-full md:w-auto !rounded-[8px] !border-[#7556FA] !px-[28px] !py-[12px] !text-[14px] !font-semibold !leading-[1.7] !text-[#7556FA] hover:!bg-white"
                onClick={() => {
                    closeMenu();
                    onSubmitIntent("save_and_pick");
                }}
            >
                <span>{t("general.buttons.saveAndStartPickingOrder")}</span>
            </UIButton>

            <div
                ref={anchorEl}
                className="relative w-full md:w-auto"
                onMouseEnter={openMenu}
                onMouseLeave={() => scheduleClose()}
            >
                {/* The main save action stays a direct UIButton click, while the chevron keeps
                    the alternate path visually attached to the primary action instead of
                    splitting the button into separate hit targets. */}
                <UIButton
                    type="button"
                    className="min-h-[48px] w-full md:w-auto min-w-[156px] !rounded-[8px] !px-[28px] !py-[12px] !text-[14px] !font-semibold !leading-[1.8]"
                    onFocus={openMenu}
                    onClick={() => {
                        closeMenu();
                        onSubmitIntent("save_order");
                    }}
                >
                    <span className="flex items-center gap-[8px]">
                        <span>{t("general.buttons.saveOrder")}</span>
                        <ChevronDown
                            size={16}
                            className="shrink-0"
                            style={{
                                transform: isMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 120ms ease-out",
                            }}
                        />
                    </span>
                </UIButton>

                <Menu
                    id="sale-order-save-menu"
                    anchorEl={anchorEl.current}
                    open={isMenuOpen}
                    onClose={closeMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    TransitionComponent={Fade}
                    transitionDuration={{ enter: 120, exit: 90 }}
                    MenuListProps={{
                        "aria-labelledby": "sale-order-save-menu",
                    }}
                    PaperProps={{
                        onMouseEnter: openMenu,
                        onMouseLeave: () => scheduleClose(),
                        sx: {
                            mt: "8px",
                            width: "296px",
                            borderRadius: "8px",
                            overflow: "hidden",
                            border: "1px solid #ECE9FF",
                            boxShadow: "-6px 5px 33px 0px rgba(59, 65, 208, 0.11)",
                        },
                    }}
                >
                    <div className="p-[8px] space-y-[8px]">
                        <SaveMenuItem
                            isActive={hoveredIntent === "save_order"}
                            isDimmed={hoveredIntent !== null && hoveredIntent !== "save_order"}
                            icon={<FileCheck2 size={16} />}
                            label={t("general.buttons.saveOrder")}
                            trailing={<Check size={20} className="text-[#7556FA]" />}
                            onHoverChange={(isHovered) => {
                                setHoveredIntent(isHovered ? "save_order" : null);
                            }}
                            onClick={() => {
                                closeMenu();
                                onSubmitIntent("save_order");
                            }}
                        />

                        <Divider color={"#D9DAFD"}/>

                        <SaveMenuItem
                            isActive={hoveredIntent === "save_offer"}
                            isDimmed={hoveredIntent !== null && hoveredIntent !== "save_offer"}
                            icon={<FileText size={16} />}
                            label={t("general.buttons.saveAsOffer")}
                            description={t("general.buttons.saveAsOfferDescription")}
                            onHoverChange={(isHovered) => {
                                setHoveredIntent(isHovered ? "save_offer" : null);
                            }}
                            onClick={() => {
                                closeMenu();
                                onSubmitIntent("save_offer");
                            }}
                        />

                        <SaveMenuItem
                            isActive={hoveredIntent === "save_prepaid"}
                            isDimmed={hoveredIntent !== null && hoveredIntent !== "save_prepaid"}
                            icon={<CreditCard size={16} />}
                            label={t("general.buttons.saveAsPrepaid")}
                            description={t("general.buttons.saveAsPrepaidDescription")}
                            onHoverChange={(isHovered) => {
                                setHoveredIntent(isHovered ? "save_prepaid" : null);
                            }}
                            onClick={() => {
                                closeMenu();
                                onSubmitIntent("save_prepaid");
                            }}
                        />
                    </div>
                </Menu>
            </div>
        </div>
    );
}

interface SaveMenuItemProps {
    icon: React.ReactNode;
    label: string;
    description?: string;
    trailing?: React.ReactNode;
    isActive?: boolean;
    isDimmed?: boolean;
    onHoverChange?: (isHovered: boolean) => void;
    onClick: () => void;
}

function SaveMenuItem(props: SaveMenuItemProps) {
    return (
        <button
            type="button"
            className={`flex w-full items-start gap-[8px] rounded-[8px] border-none px-[8px] py-[10px] text-left transition-all duration-150 ${
                props.isActive ? "bg-[#7556FA]" : "bg-transparent"
            } ${props.isDimmed ? "opacity-45" : "opacity-100"}`}
            onMouseEnter={() => props.onHoverChange?.(true)}
            onMouseLeave={() => props.onHoverChange?.(false)}
            onFocus={() => props.onHoverChange?.(true)}
            onBlur={() => props.onHoverChange?.(false)}
            onClick={props.onClick}
        >
            <span
                className={`mt-[1px] flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[4px] transition-colors duration-150 ${
                    props.isActive ? "bg-white text-[#7556FA]" : "bg-[#ECEDFE] text-[#7556FA]"
                }`}
            >
                {props.icon}
            </span>

            <span className="min-w-0 grow">
                <span
                    className={`block text-[16px] font-light leading-[1.5] transition-colors duration-150 ${
                        props.isActive ? "text-white" : "text-accent"
                    }`}
                >
                    {props.label}
                </span>
                {props.description && (
                    <span
                        className={`block text-[14px] font-light leading-[1.7] transition-colors duration-150 ${
                            props.isActive ? "text-white/80" : "text-secondary"
                        }`}
                    >
                        {props.description}.
                    </span>
                )}
            </span>

            {props.trailing && (
                <span
                    className={`flex shrink-0 items-center self-center transition-opacity duration-150 [&_svg]:transition-colors [&_svg]:duration-150 ${
                        props.isActive ? "[&_svg]:!text-white" : "[&_svg]:!text-[#7556FA]"
                    }`}
                >
                    {props.trailing}
                </span>
            )}
        </button>
    );
}
